import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Circle, Rocket, ArrowRight } from "lucide-react";
import { restaurantApi, kycApi } from "../../lib/api";

// Guided go-live tracker for restaurants: menu, hours, photos and KYC.
// Auto-hides once every step is complete.
export default function RestaurantOnboarding({ restaurant }) {
  const [menuCount, setMenuCount] = useState(null);
  const [kycStatus, setKycStatus] = useState(null);
  useEffect(() => {
    restaurantApi.menu().then((m) => setMenuCount((m || []).length)).catch(() => setMenuCount(0));
    kycApi.mine().then((d) => setKycStatus(d.status || "unverified")).catch(() => setKycStatus(null));
  }, []);

  if (!restaurant || menuCount === null || kycStatus === null) return null;

  const steps = [
    { key: "details", label: "Add your business details & map location", to: "/app/restaurant/info", done: !!(restaurant.name && restaurant.address && restaurant.lat) },
    { key: "menu", label: "Add dishes to your menu", to: "/app/restaurant/menu", done: menuCount > 0 },
    { key: "hours", label: "Set your opening hours", to: "/app/restaurant/info", done: !!(restaurant.hours && Object.keys(restaurant.hours).length > 0) },
    { key: "photos", label: "Add storefront photos", to: "/app/restaurant/info", done: (restaurant.gallery || []).length > 0 },
    { key: "kyc", label: "Complete KYC verification", to: "/app/restaurant/info", done: kycStatus === "verified" },
  ];
  const doneCount = steps.filter((s) => s.done).length;
  if (doneCount === steps.length) return null;
  const next = steps.find((s) => !s.done);

  return (
    <div className="mb-5 rounded-2xl border border-brand-200 bg-brand-50/50 p-5" data-testid="restaurant-onboarding">
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white text-brand"><Rocket className="h-5 w-5" /></span>
        <div className="flex-1">
          <p className="font-semibold text-ink">Finish setting up to go live</p>
          <p className="text-sm text-ink-soft">{doneCount} of {steps.length} steps done — complete them so customers can order from you.</p>
        </div>
      </div>
      <div className="mt-3 h-2 overflow-hidden rounded-full bg-white/70"><div className="h-full rounded-full bg-brand transition-all duration-500" style={{ width: `${(doneCount / steps.length) * 100}%` }} /></div>
      <ul className="mt-4 space-y-1.5">
        {steps.map((s) => (
          <li key={s.key} data-testid={`onboarding-step-${s.key}`}>
            <Link to={s.to} className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-white/60">
              {s.done ? <CheckCircle2 className="h-4 w-4 shrink-0 text-green-600" /> : <Circle className="h-4 w-4 shrink-0 text-brand" />}
              <span className={s.done ? "text-ink-soft line-through" : "font-medium text-ink"}>{s.label}</span>
              {!s.done && <ArrowRight className="ml-auto h-4 w-4 text-brand" />}
            </Link>
          </li>
        ))}
      </ul>
      {next && (
        <Button2 to={next.to} label={`Next: ${next.label}`} />
      )}
    </div>
  );
}

function Button2({ to, label }) {
  return (
    <Link to={to} className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600" data-testid="onboarding-next-btn">
      {label} <ArrowRight className="h-4 w-4" />
    </Link>
  );
}
